import { AnimatePresence, m } from "framer-motion";
import { Suspense, useCallback, useRef, useState } from "react";
import { Routes, useLocation } from "react-router";
import { mainLayoutRouteElements } from "@/app/mainLayoutRoutes";
import { pageTransition, pageVariants } from "@/shared/components/page-animation/AnimatedPage";

function getPageLayerKey(pathname: string, search: string): string {
  if (pathname === "/news" || pathname.startsWith("/news/")) {
    return "/news";
  }
  return `${pathname}${search}`;
}

function persistExitedPageLayer(node: HTMLElement | null) {
  if (!node) return;
  node.style.opacity = "0";
  node.style.visibility = "hidden";
}

function scrollToPageTop() {
  window.scrollTo(0, 0);
}

/**
 * Each navigation mount gets a snapshot of `location` for inner `Routes`, so the exiting
 * layer keeps showing the previous page while `Outlet` would already switch to the next.
 * Same-layer navigations (news list ↔ detail) keep the layout mounted and use live location.
 */
function FrozenMainRoutesMotion() {
  const location = useLocation();
  const layerRef = useRef<HTMLDivElement>(null);
  const [frozenLocation] = useState(() => location);
  const routesLocation =
    getPageLayerKey(location.pathname, location.search) ===
    getPageLayerKey(frozenLocation.pathname, frozenLocation.search)
      ? location
      : frozenLocation;

  const handleAnimationComplete = useCallback((definition: unknown) => {
    if (definition === "out") {
      persistExitedPageLayer(layerRef.current);
    }
  }, []);

  return (
    <m.div
      ref={layerRef}
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      onAnimationComplete={handleAnimationComplete}
      className="animation-wrapper relative w-full"
    >
      <Suspense fallback={<div className="min-h-[50svh]" aria-hidden />}>
        <Routes location={routesLocation}>{mainLayoutRouteElements}</Routes>
      </Suspense>
    </m.div>
  );
}

export function HeaderAnimatedOutlet() {
  const location = useLocation();
  const layerKey = getPageLayerKey(location.pathname, location.search);

  return (
    <AnimatePresence mode="wait" onExitComplete={scrollToPageTop}>
      <FrozenMainRoutesMotion key={layerKey} />
    </AnimatePresence>
  );
}
